import React, { useState, useEffect } from "react";
import SEO from "../seo";
const LandingPageGenerate = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "method-1-existing-product",
        "method-2-landing-pages-section",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Generate a Landing Page | xPage Help Center"
        description="Learn how to generate high-converting landing pages in Xpage from your product catalog or the Landing Pages section. Step-by-step guidance for efficient page creation."
        keywords="xPage, generate landing page, landing pages, product catalog, Xpage Builder, page creation, customize landing page"
        canonical="https://help.xpage.ai/help/store-operation/Landing%20Page%20Generate"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Generate a Landing PageNativeWind
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage makes it simple to turn your products into high-converting
            landing pages. You can generate a page in two ways: directly from
            your product catalog or from the Landing Pages section.
          </p>
        </header>

        {/* Method 1: From an Existing Product Section */}
        <section id="method-1-existing-product" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Method 1: From an Existing Product
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the left sidebar, go to Products → All Products.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the product you'd like to create a landing page for.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Make any updates if needed (such as images, details, or
                  descriptions).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Generate Landing Page in the top right corner.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Answer a few onboarding questions to tailor the page to your
                  goals.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  You'll be redirected to the Xpage Builder, where your landing
                  page is ready to customize and publish.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Method 2: From the Landing Pages Section */}
        <section
          id="method-2-landing-pages-section"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Method 2: From the Landing Pages Section
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  In the left sidebar, click Landing Pages.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the top right, select Generate Landing Page.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Choose one of the following options:
                </span>
                <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-6 mt-2">
                  <li>
                    <strong>From Existing Product:</strong> Select a product
                    from your catalog and click Generate.
                  </li>
                  <li>
                    <strong>From Product Link:</strong> Paste the product URL,
                    choose a market angle and language, then click Generate.
                  </li>
                </ul>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Complete the onboarding questions to personalize your page.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  You'll arrive in the Builder, where your landing page is ready
                  to refine and launch.
                </span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">TIP:</span> Whichever method you
                choose, the Builder gives you full control to adjust sections,
                visuals, and content before going live.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#method-1-existing-product"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "method-1-existing-product"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Method 1: From Existing Product
              </a>
            </li>
            <li>
              <a
                href="#method-2-landing-pages-section"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "method-2-landing-pages-section"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Method 2: From Landing Pages
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LandingPageGenerate;
